import { useEffect, useRef, useMemo } from 'react';
import { Marker, Popup, type Map as MaplibreMap } from 'maplibre-gl';

/**
 * A photo location to render as a thumbnail marker on a MapLibre map.
 */
export interface PhotoLocation {
  id: string;
  /** Thumbnail / preview URL (blob URL, data URL, or remote URL). */
  url: string;
  latitude: number;
  longitude: number;
  /** Optional label shown in the popup header (e.g. "Photo 1", camera model). */
  label?: string;
  /** ISO date string of when the photo was taken (EXIF dateTimeOriginal). */
  timestamp?: string;
}

/**
 * Format an ISO timestamp into a human-readable date/time string.
 * Returns null when the input is empty or invalid.
 */
function formatTimestamp(ts?: string): string | null {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Build the HTML content for a photo marker popup.
 */
function buildPopupHtml(loc: PhotoLocation): string {
  const ts = formatTimestamp(loc.timestamp);
  const label = loc.label ?? 'Photo';
  const latStr = loc.latitude.toFixed(5);
  const lngStr = loc.longitude.toFixed(5);

  return `
    <div style="font-family: inherit; max-width: 220px;">
      <img src="${loc.url}" alt="${label}"
        style="width:100%;height:140px;object-fit:cover;border-radius:6px;margin-bottom:8px;" />
      <div style="font-weight:600;font-size:13px;margin-bottom:4px;">${label}</div>
      ${ts ? `<div style="font-size:12px;color:#6b7280;margin-bottom:4px;">📸 ${ts}</div>` : ''}
      <div style="font-size:12px;color:#6b7280;">
        📍 ${latStr}, ${lngStr}
      </div>
    </div>`;
}

/**
 * Build a thumbnail DOM element for a photo marker.
 * The element is a circular bordered image with a camera-badge style.
 */
function buildMarkerElement(loc: PhotoLocation): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'photo-map-marker';
  el.style.cssText = [
    'width:44px',
    'height:44px',
    'border-radius:50%',
    'border:3px solid #fff',
    'overflow:hidden',
    'cursor:pointer',
    'box-shadow:0 2px 8px rgba(0,0,0,0.35)',
    'background:#e5e7eb',
    'transition:transform 0.15s ease',
  ].join(';');

  const img = document.createElement('img');
  img.src = loc.url;
  img.alt = loc.label ?? 'Photo';
  img.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;';
  img.loading = 'lazy';
  el.appendChild(img);

  // Scale up on hover for better UX (desktop).
  el.addEventListener('mouseenter', () => {
    el.style.transform = 'scale(1.15)';
  });
  el.addEventListener('mouseleave', () => {
    el.style.transform = 'scale(1)';
  });

  return el;
}

/**
 * React hook that manages photo thumbnail markers on a MapLibre map.
 *
 * Adds a `Marker` (with custom thumbnail element) and an attached `Popup`
 * for each photo location. Markers are cleaned up and re-created whenever
 * the map instance or the photo locations change.
 *
 * Safe to call before the map has loaded its style — markers are added
 * on the `load` event if the style isn't ready yet.
 */
export function usePhotoMarkers(
  map: MaplibreMap | null,
  photoLocations: PhotoLocation[],
): void {
  const markersRef = useRef<Marker[]>([]);

  // Serialize the photo locations so the effect only re-runs when
  // the actual content changes, not the array identity.
  const locationsKey = useMemo(
    () => JSON.stringify(photoLocations),
    [photoLocations],
  );

  useEffect(() => {
    if (!map) return;

    // Clear any existing photo markers.
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    if (photoLocations.length === 0) return;

    const addMarkers = () => {
      for (const loc of photoLocations) {
        const el = buildMarkerElement(loc);
        const marker = new Marker({ element: el })
          .setLngLat([loc.longitude, loc.latitude])
          .addTo(map);

        const popup = new Popup({ maxWidth: '260px', closeButton: true })
          .setHTML(buildPopupHtml(loc));

        marker.setPopup(popup);
        markersRef.current.push(marker);
      }
    };

    if (map.isStyleLoaded()) {
      addMarkers();
    } else {
      map.once('load', addMarkers);
    }

    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, locationsKey]);
}
